import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import Table from "components/ui/Table";
import BiodataDetailsView from "components/BiodataDetailsView";
import { useAuth } from "context/AuthContext";
import { employerAPI, biodataAPI } from "utils/api";
import { useBiodataCache } from "context/BiodataCacheContext";
import Notifier from "components/ui/Notifier";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const calculateAge = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const diff = Date.now() - date.getTime();
  const ageDate = new Date(diff);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

const formatGender = (value) => {
  if (!value) return "";
  const lower = String(value).toLowerCase();
  if (lower === "male" || lower === "female") {
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
  return String(value);
};

const summarizeSkills = (skills = "", fallback = "") => {
  const parsed = skills
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (parsed.length > 0) return parsed;
  if (!fallback) return [];
  return [fallback];
};

const EmployerBiodataSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const navigationState = location.state || {};
  const initialApplication = navigationState.vdrApplication;
  const replaceWorkerId = navigationState.replaceWorkerId;
  const replaceWorkerName = navigationState.replaceWorkerName;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [application, setApplication] = useState(initialApplication || null);
  const [workers, setWorkers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    total_pages: 1,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [skillFilter, setSkillFilter] = useState("");
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [activeBiodata, setActiveBiodata] = useState(null);
  const [notify, setNotify] = useState({
    show: false,
    type: "info",
    message: "",
  });
  const [selectionData, setSelectionData] = useState(null);
  const [submittedWorkerIds, setSubmittedWorkerIds] = useState([]);
  const {
    getAllBiodatas,
    invalidateOnUpdate,
    getPhotoUrl,
    prefetchPhotoUrlsLimited,
    prefetchPhotoUrl,
  } = useBiodataCache();
  const pageCacheRef = React.useRef(new Map());
  const CACHE_MS = 5 * 60 * 1000;
  const clearWorkerCache = () => pageCacheRef.current.clear();

  const vdrId =
    application?.vdrId ||
    navigationState.vdrApplication?.vdrId ||
    navigationState.vdr_id;
  const totalSlots = useMemo(() => {
    return (
      application?.slots?.approved ??
      application?.slots?.requested ??
      application?.fclRequested ??
      selectionData?.fcl_count ??
      0
    );
  }, [application?.fclRequested, application?.slots?.approved, application?.slots?.requested, selectionData?.fcl_count]);

  const submittedSlots = useMemo(() => {
    const usedFromSlots =
      application?.slots?.used ??
      Math.max(
        totalSlots -
          (application?.slots?.remaining ??
            application?.slots?.localRemaining ??
            totalSlots),
        0
      );
    return Math.max(usedFromSlots, 0);
  }, [application?.slots?.localRemaining, application?.slots?.remaining, application?.slots?.used, totalSlots]);

  const remainingSlots = useMemo(
    () => Math.max(totalSlots - submittedSlots, 0),
    [submittedSlots, totalSlots]
  );

  const availableSlots = useMemo(() => {
    const rawAvailable =
      application?.slots?.available ??
      application?.slots?.localRemaining ??
      remainingSlots;
    return Math.max(Math.min(rawAvailable, remainingSlots), 0);
  }, [application?.slots?.available, application?.slots?.localRemaining, remainingSlots]);

  const maxSelectableNow = useMemo(
    () => (replaceWorkerId ? 1 : availableSlots),
    [availableSlots, replaceWorkerId]
  );

  // Shortlisting is allowed beyond remaining quota; submission is constrained on the shortlist page
  const submissionLocked = false;

  useEffect(() => {
    if (user && (!user.eclabUser || user.role !== 3)) {
      navigate("/login-screen");
    }
  }, [user, navigate]);

  useEffect(() => {
    const ensureApplication = async () => {
      if (!initialApplication && vdrId) {
        try {
          const data = await employerAPI.getApplication(vdrId);
          setApplication(data);
        } catch (err) {
          console.error("Failed to load VDR application", err);
          setError(
            err.response?.data?.error ||
              err.message ||
              "Failed to load application"
          );
        }
      }
    };

    ensureApplication();
  }, [initialApplication, vdrId]);

  const loadSelection = useCallback(async () => {
    if (!vdrId) return;
    try {
      const response = await employerAPI.getSelections(vdrId);
      setSelectionData(response);
    } catch (err) {
      console.error("Failed to load selection info", err);
    }
  }, [vdrId]);

  useEffect(() => {
    if (!selectionData) return;
    const selectedFromSubmission =
      selectionData?.selected_workers
        ?.map((worker) => worker.biodata_id)
        .filter(Boolean) || [];
    setSubmittedWorkerIds(selectedFromSubmission);
    setSelectedWorkers((prev) =>
      prev.filter((id) => !selectedFromSubmission.includes(id))
    );
  }, [selectionData]);

  const loadWorkers = useCallback(
    async (pageParam = 1, preserveSelection = false) => {
      if (!vdrId) return;
      try {
        setLoading(true);
        setError(null);
        const params = {
          page: pageParam,
          limit: 50,
          search: searchTerm.trim() || undefined,
        };
        const cacheKey = `${vdrId}|${params.search || ""}|${pageParam}|${
          params.limit
        }`;
        const cached = pageCacheRef.current.get(cacheKey);
        const now = Date.now();
        if (!preserveSelection && cached && now - cached.timestamp < CACHE_MS) {
          const workerRecords = cached.workers;
          setWorkers(workerRecords);
          setPagination(cached.pagination);
          prefetchPhotoUrlsLimited(workerRecords, 8);
        } else {
          const response = await employerAPI.getAvailableWorkers(vdrId, params);

          const recordsCount =
            response?.workers?.length ||
            response?.records?.length ||
            response?.data?.workers?.length ||
            response?.data?.records?.length ||
            response?.data?.length ||
            (Array.isArray(response) ? response.length : 0);

          // Handle different response formats (legacy + refactored API)
          const workerRecords =
            (Array.isArray(response?.workers) && response.workers) ||
            (Array.isArray(response?.records) && response.records) ||
            (Array.isArray(response?.data?.workers) && response.data.workers) ||
            (Array.isArray(response?.data?.records) && response.data.records) ||
            (Array.isArray(response?.data) && response.data) ||
            (Array.isArray(response) && response) ||
            [];

          setWorkers(workerRecords);
          const newPagination = response?.pagination ||
            response?.data?.pagination || {
              page: pageParam,
              limit: 50,
              total: workerRecords.length,
              total_pages: 1,
            };
          setPagination(newPagination);
          pageCacheRef.current.set(cacheKey, {
            workers: workerRecords,
            pagination: newPagination,
            timestamp: Date.now(),
          });
          prefetchPhotoUrlsLimited(workerRecords, 8);

          // Preserve selections when searching or paginating
          setSelectedWorkers((prevSelected) => {
            if (submissionLocked) {
              return prevSelected;
            }
            const reserved = workerRecords
              .filter((worker) => worker.is_reserved_by_self)
              .map((worker) => worker.id);

            if (!prevSelected || prevSelected.length === 0) {
              return reserved;
            }

            // When preserving selection (search/pagination), keep previous selections (excluding already submitted)
            if (preserveSelection) {
              const preserved = prevSelected.filter(
                (id) => !submittedWorkerIds.includes(id)
              );
              return Array.from(new Set([...preserved, ...reserved]));
            }

            const stillValid = prevSelected.filter((id) =>
              workerRecords.some(
                (worker) => worker.id === id && worker.is_reserved_by_self
              )
            );
            return Array.from(new Set([...stillValid, ...reserved]));
          });
        }
      } catch (err) {
        console.error("Failed to load workers", err);
        setError(
          err.response?.data?.error ||
            err.message ||
            "Failed to load available workers"
        );
      } finally {
        setLoading(false);
      }
    },
    [loadSelection, searchTerm, submissionLocked, submittedWorkerIds, vdrId]
  );

  useEffect(() => {
    if (vdrId) {
      loadSelection();
      // Use preserveSelection=true to maintain user selections across searches
      loadWorkers(1, true);
    }
  }, [vdrId, loadSelection, searchTerm]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleWorkerReservation = useCallback(
    async (worker) => {
      if (submissionLocked) {
        setNotify({
          show: true,
          type: "warning",
          message: "No available slots left for this quota.",
        });
        return;
      }
      try {
        setSubmitting(true);
        const wasReserved = worker.is_reserved_by_self;

        if (wasReserved && worker.shortlist_id) {
          // Releasing - remove from selection
          await employerAPI.releaseShortlist(vdrId, worker.shortlist_id);
          setSelectedWorkers((prev) => prev.filter((id) => id !== worker.id));
          setNotify({
            show: true,
            type: "success",
            message: "Reservation released.",
          });
          setWorkers((prev) =>
            prev.map((w) =>
              w.id === worker.id
                ? {
                    ...w,
                    is_reserved_by_self: false,
                    reserved_until: null,
                    shortlist_id: null,
                  }
                : w
            )
          );
        } else {
          const shortlistResp = await employerAPI.createShortlist(vdrId, worker.id);
          const shortlistPayload =
            shortlistResp?.data?.data ||
            shortlistResp?.data ||
            shortlistResp ||
            {};
          const shortlistId = shortlistPayload.id || shortlistPayload.shortlist_id || shortlistPayload.data?.id;
          const reservedUntil =
            shortlistPayload.reserved_until ||
            shortlistPayload.hold_expires_at ||
            shortlistPayload.holdExpiresAt ||
            new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
          setNotify({
            show: true,
            type: "success",
            message: "Worker reserved.",
          });

          // Automatically add to selection after reserving
          setSelectedWorkers((prev) => {
            if (replaceWorkerId) {
              return [worker.id];
            }
            if (!prev.includes(worker.id)) {
              return [...prev, worker.id];
            }
            return prev;
          });
          setWorkers((prev) =>
            prev.map((w) =>
              w.id === worker.id
                ? {
                    ...w,
                    is_reserved_by_self: true,
                    reserved_until: reservedUntil,
                    shortlist_id: shortlistId || w.shortlist_id || worker.shortlist_id,
                  }
                : w
            )
          );
        }

        clearWorkerCache();
        await loadWorkers(pagination?.page || 1, true);
        await loadSelection();
      } catch (err) {
        console.error("Shortlist toggle failed", err);
        setError(
          err.response?.data?.error ||
            err.message ||
            "Unable to update shortlist"
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      submissionLocked,
      vdrId,
      loadWorkers,
      loadSelection,
      pagination?.page,
      replaceWorkerId,
      selectedWorkers.length,
      totalSlots,
    ]
  );

  const toggleWorkerSelection = useCallback(
    (worker) => {
      if (submissionLocked) {
        setNotify({
          show: true,
          type: "warning",
          message: "No available slots left for this quota.",
        });
        return;
      }
      const isReserved =
        worker.is_reserved_by_self || Boolean(worker.shortlist_id);
      if (!isReserved) {
        setNotify({
          show: true,
          type: "warning",
          message: "Reserve the worker before adding them to your selection.",
        });
        return;
      }

      setSelectedWorkers((prev) => {
        const exists = prev.includes(worker.id);
        if (exists) {
          return prev.filter((id) => id !== worker.id);
        }

        if (replaceWorkerId) {
          return [worker.id];
        }

        return [...prev, worker.id];
      });
    },
    [replaceWorkerId]
  );

  const handlePageChange = useCallback(
    (nextPage) => {
      // Preserve selections when changing pages
      loadWorkers(nextPage, true);
    },
    [loadWorkers]
  );

  const handleViewDetails = useCallback(async (worker) => {
    if (!worker?.id) return;
    setDetailError(null);
    setDetailLoading(true);
    setActiveBiodata(null);
    setDetailModalOpen(true);
    try {
      const result = await biodataAPI.getBiodataById(worker.id);
      if (result.status === "success" && result.data) {
        setActiveBiodata(result.data);
      } else {
        setDetailError(result.message || "Failed to load biodata details.");
      }
    } catch (err) {
      console.error("Failed to fetch biodata details", err);
      setDetailError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load biodata details."
      );
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailLoading(false);
    setDetailError(null);
    setActiveBiodata(null);
  };

  const handleSubmitSelection = async () => {
    // Submission is handled from shortlist page; keep this noop to avoid accidental calls.
    setNotify({
      show: true,
      type: "info",
      message: "Submit your reserved workers from the Shortlisted Workers page.",
    });
  };

  const filteredWorkers = useMemo(() => {
    return workers.filter((worker) => {
      if (skillFilter) {
        const haystack = `${worker.skills || ""} ${
          worker.experience || ""
        }`.toLowerCase();
        if (!haystack.includes(skillFilter.toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  }, [workers, skillFilter]);

  const selectedWorkerSummaries = useMemo(() => {
    return workers.filter((worker) => selectedWorkers.includes(worker.id));
  }, [workers, selectedWorkers]);

  const tableColumns = useMemo(
    () => [
      {
        label: "Worker",
        render: (worker) => {
          const age = calculateAge(worker.date_of_birth);
          const badges = [];
          if (worker.is_reserved_by_self) {
            badges.push({
              label: "Reserved",
              className: "bg-amber-100 text-amber-700",
            });
          }
          if (worker.is_submitted_by_self) {
            badges.push({
              label: "Submitted",
              className: "bg-green-100 text-green-700",
            });
          }

          return (
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center"
                data-worker-id={worker.id}
              >
                <Image
                  src={getPhotoUrl(worker.id, worker)}
                  alt={worker.full_name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="font-semibold text-gray-900 text-sm">
                  {worker.full_name}
                </div>
                <div className="text-xs text-gray-500">
                  Passport: {worker.passport_no || "—"}
                </div>
                {worker.worker_id && (
                  <div className="text-xs text-gray-500">
                    Worker ID: {worker.worker_id}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-1">
                  {badges.map((badge) => (
                    <span
                      key={badge.label}
                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                  ))}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {worker.nationality}
                  {worker.gender ? ` • ${formatGender(worker.gender)}` : ""}
                  {age ? ` • ${age} yrs` : ""}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        label: "Medical & Availability",
        render: (worker) => (
          <div className="text-sm text-gray-600 space-y-2">
            <div>
              <div className="text-[11px] uppercase text-gray-400 tracking-wide">
                Medical Expiry
              </div>
              <div className="font-medium text-gray-900">
                {worker.medical_expiry_date
                  ? formatDate(worker.medical_expiry_date)
                  : "N/A"}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-gray-400 tracking-wide">
                Reserved Until
              </div>
              <div className="font-medium text-gray-900">
                {worker.reserved_until
                  ? formatDate(worker.reserved_until)
                  : "Not reserved"}
              </div>
            </div>
          </div>
        ),
      },
      {
        label: "Skills & Experience",
        render: (worker) => {
          const skills = summarizeSkills(worker.skills, worker.experience);
          return (
            <div className="flex flex-wrap gap-1 max-w-xs">
              {skills.length > 0 ? (
                skills.slice(0, 5).map((skill, index) => (
                  <span
                    key={`${skill}-${index}`}
                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                  >
                    {skill}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-500">No skills listed</span>
              )}
            </div>
          );
        },
      },
      {
        label: "VDR Type",
        render: (worker) => (
          <div className="text-sm text-gray-600">
            <div className="text-[11px] uppercase text-gray-400 tracking-wide">
              VDR Type
            </div>
            <div className="font-medium text-gray-900">
              {worker.vdr_type || application?.vdrType || "—"}
            </div>
          </div>
        ),
      },
    ],
    [selectedWorkers, application?.vdrType]
  );

  const tablePagination = useMemo(() => {
    if (!pagination || !pagination.total_pages || pagination.total_pages <= 1)
      return null;
    return {
      currentPage: pagination.page || 1,
      totalPages: pagination.total_pages,
      onPageChange: handlePageChange,
    };
  }, [pagination, handlePageChange]);

  const renderActions = useCallback(
    (worker) => {
      return (
        <div className="flex flex-col gap-2 min-w-[180px]">
          <button
            onClick={() => handleViewDetails(worker)}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm font-medium flex items-center justify-center"
          >
            <Icon name="Eye" size={14} className="mr-1" />
            View Details
          </button>
          <button
            onClick={() => toggleWorkerReservation(worker)}
            disabled={submitting}
            className={`w-full px-3 py-2 rounded text-sm font-medium transition-colors ${
              worker.is_reserved_by_self
                ? "bg-red-100 text-red-700 hover:bg-red-200"
                : "bg-blue-600 text-white hover:bg-blue-700"
            } ${submitting ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {worker.is_reserved_by_self ? "Release" : "Reserve"}
          </button>
        </div>
      );
    },
    [handleViewDetails, toggleWorkerReservation, submitting]
  );

  useEffect(() => {
    if (!workers || workers.length === 0) return;
    const els = document.querySelectorAll("[data-worker-id]");
    if (!els || els.length === 0) return;
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idAttr = entry.target.getAttribute("data-worker-id");
          const id = Number(idAttr) || idAttr;
          if (!id || seen.has(id)) return;
          const rec = workers.find((w) => w.id === id);
          const direct =
            rec?.photo_url ||
            rec?.photoUrl ||
            rec?.primary_photo_url ||
            rec?.primaryPhotoUrl;
          const cached = getPhotoUrl(id, rec);
          if (!direct && !cached) prefetchPhotoUrl(id);
          seen.add(id);
          observer.unobserve(entry.target);
        });
      },
      { root: null, threshold: 0.25 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [workers, getPhotoUrl, prefetchPhotoUrl]);

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderBar
        userRole="employer"
        onSidebarToggle={handleSidebarToggle}
        userName={user?.fullName || "Employer"}
      />

      <Notifier
        type={notify.type}
        message={notify.message}
        show={notify.show}
        onClose={() => setNotify((prev) => ({ ...prev, show: false }))}
        duration={3000}
      />

      <RoleBasedSidebar
        userRole="employer"
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main
        className={`transition-all duration-300 ease-in-out pt-16 pb-32 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-8 space-y-6">
          <BreadcrumbNavigation
            items={[
              { label: "Dashboard", path: "/employer-dashboard" },
              {
                label: "Application Management",
                path: "/employer-application-management",
              },
              {
                label: application?.vdrId || vdrId || "Selection",
                path: location.pathname,
              },
            ]}
          />

          {application && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {replaceWorkerId
                      ? `Replace Worker in ${application.vdrId}`
                      : `Select Workers for ${application.vdrId}`}
                  </h1>
                  <p className="text-gray-600">{application.companyName}</p>
                  <div className="text-sm text-gray-500 mt-1">
                    Application ID: {application.applicationId}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-4 lg:mt-0">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <Icon name="Globe" size={14} className="mr-1" />
                    {application.nationality}
                  </span>
                  {application.vdrType && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                      <Icon name="Layers" size={14} className="mr-1" />
                      {application.vdrType}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      application.biodataTypeLabel === "CLAB"
                        ? "bg-primary-100 text-primary-700"
                        : "bg-secondary-100 text-secondary-700"
                    }`}
                  >
                    {application.biodataTypeLabel}
                  </span>
                </div>
              </div>

              {replaceWorkerId && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                  Replacing{" "}
                  <span className="font-semibold">{replaceWorkerName}</span>.
                  Select one replacement worker.
                </div>
              )}

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                <div>
                  <span className="text-gray-400 block">Total Quota</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {totalSlots || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block">Submitted / Approved</span>
                  <span className="text-lg font-semibold text-blue-600">
                    {submittedSlots}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block">Remaining Quota</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {remainingSlots}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block">Available Now</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {maxSelectableNow}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="text-gray-400 block">Quota Expiry</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {formatDate(application.validityStartDate)} –{" "}
                    {formatDate(application.validityEndDate)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block">Payment</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {application.paymentStatus || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Search
                </label>
                <div className="relative">
                  <Icon
                    name="Search"
                    size={16}
                    className="absolute left-3 top-3 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search by name, passport, or ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Skill Keyword
                </label>
                <input
                  type="text"
                  placeholder="e.g. welding, scaffolding"
                  value={skillFilter}
                  onChange={(e) => setSkillFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div></div>
            </div>
          </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Icon
            name="Loader"
            size={48}
            className="animate-spin text-blue-600 mx-auto mb-4"
              />
              <p className="text-gray-600 text-lg">
                Loading available workers...
              </p>
            </div>
          ) : filteredWorkers.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
              <Icon
                name="Users"
                size={48}
                className="text-gray-300 mx-auto mb-4"
              />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Workers Found
              </h3>
              <p className="text-gray-500">
                {searchTerm || skillFilter
                  ? "Try adjusting your search or filters."
                  : "No approved workers are currently available."}
              </p>
            </div>
          ) : (
            <Table
              data={filteredWorkers}
              columns={tableColumns}
              actions={renderActions}
              className="border border-gray-200 shadow-sm"
              pagination={tablePagination}
            />
          )}
        </div>
      </main>

      {selectedWorkers.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-50">
          <div
            className={`max-w-7xl mx-auto ${
              sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
            }`}
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="text-sm text-gray-500">
                  {replaceWorkerId
                    ? "1 replacement selected"
                    : `${selectedWorkers.length} worker${selectedWorkers.length === 1 ? "" : "s"} selected (remaining ${Math.max(remainingSlots - selectedWorkers.length, 0)} of ${totalSlots})`}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedWorkerSummaries.map((worker) => (
                    <span
                      key={worker.id}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                    >
                      {worker.full_name}
                      <button
                        onClick={() => toggleWorkerSelection(worker)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-500">
                  Reserved workers will appear in Shortlisted Workers. Submit batches from there.
                </div>
                <button
                  onClick={() => navigate("/employer-shortlist")}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700"
                >
                  Go to Shortlisted Workers
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailModalOpen && (
        <>
          <style>{`
            @media print {
              .biodata-modal-overlay {
                display: none !important;
              }
              .biodata-modal-content {
                position: static !important;
                max-width: 100% !important;
                max-height: none !important;
                box-shadow: none !important;
              }
            }
          `}</style>
          <div className="biodata-modal-overlay fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="biodata-modal-content bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Biodata Details
                </h2>
                <button
                  onClick={closeDetailModal}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[78vh]">
                {detailLoading ? (
                  <div className="flex items-center justify-center py-12 text-gray-600">
                    <Icon
                      name="Loader"
                      size={32}
                      className="animate-spin mr-3"
                    />
                    Loading biodata...
                  </div>
                ) : detailError ? (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
                    {detailError}
                  </div>
                ) : activeBiodata ? (
                  <BiodataDetailsView
                    biodata={activeBiodata}
                    title="Biodata Overview"
                  />
                ) : (
                  <div className="text-sm text-gray-500">
                    No biodata details available.
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmployerBiodataSelection;
